import React, { useState, useEffect } from 'react';
import { showToast } from '../../utils/apiClient';

const API = process.env.REACT_APP_BACKEND_URL;

const SimpleAnalyticsView = ({ onClose }) => {
  const [analyticsData, setAnalyticsData] = useState(null);
  const [transportData, setTransportData] = useState(null);
  const [euthanasiaData, setEuthanasiaData] = useState(null);
  const [referralData, setReferralData] = useState(null);
  const [bestSellersData, setBestSellersData] = useState(null);
  const [paymentData, setPaymentData] = useState(null);
  const [vetClinicData, setVetClinicData] = useState(null);
  const [petTypeData, setPetTypeData] = useState(null);
  const [turnaroundData, setTurnaroundData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState({});
  const [exportFilter, setExportFilter] = useState('all');
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showBusinessExport, setShowBusinessExport] = useState(false);
  const [businessExporting, setBusinessExporting] = useState(false);
  const [selectedReports, setSelectedReports] = useState({
    vet_clinics: true, referral_sources: true, monthly_performance: true,
    payment_methods: true, pet_types: true, postcodes: true, turnaround: true
  });

  const getCurrentWeekDates = () => {
    const nowAEST = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
    const dayOfWeek = nowAEST.getDay();
    const daysSinceMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const monday = new Date(nowAEST);
    monday.setDate(nowAEST.getDate() - daysSinceMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: fmt(monday), end: fmt(sunday) };
  };

  const getPreset = (preset) => {
    const nowAEST = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = fmt(nowAEST);
    if (preset === 'this_week') {
      const dow = nowAEST.getDay();
      const monday = new Date(nowAEST);
      monday.setDate(nowAEST.getDate() - (dow === 0 ? 6 : dow - 1));
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      return { start: fmt(monday), end: fmt(sunday) };
    }
    if (preset === 'last_week') {
      const dow = nowAEST.getDay();
      const thisMonday = new Date(nowAEST);
      thisMonday.setDate(nowAEST.getDate() - (dow === 0 ? 6 : dow - 1));
      const lastMonday = new Date(thisMonday); lastMonday.setDate(thisMonday.getDate() - 7);
      const lastSunday = new Date(thisMonday); lastSunday.setDate(thisMonday.getDate() - 1);
      return { start: fmt(lastMonday), end: fmt(lastSunday) };
    }
    if (preset === 'this_month') {
      return { start: fmt(new Date(nowAEST.getFullYear(), nowAEST.getMonth(), 1)), end: today };
    }
    if (preset === 'last_month') {
      return { start: fmt(new Date(nowAEST.getFullYear(), nowAEST.getMonth() - 1, 1)), end: fmt(new Date(nowAEST.getFullYear(), nowAEST.getMonth(), 0)) };
    }
    if (preset === 'last_30') {
      const from = new Date(nowAEST); from.setDate(nowAEST.getDate() - 30);
      return { start: fmt(from), end: today };
    }
    if (preset === 'this_year') {
      return { start: `${nowAEST.getFullYear()}-01-01`, end: today };
    }
    if (preset === 'all_time') {
      return { start: '2024-01-01', end: today };
    }
    return getCurrentWeekDates();
  };

  const [dateRange, setDateRange] = useState(getPreset('this_week'));

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ start_date: dateRange.start, end_date: dateRange.end });
        const endpoints = [
          fetch(`${API}/api/analytics/dashboard?${params}`),
          fetch(`${API}/api/analytics/referral-sources?${params}`),
          fetch(`${API}/api/analytics/best-sellers?${params}`),
          fetch(`${API}/api/analytics/payment-methods?${params}`),
          fetch(`${API}/api/analytics/vet-clinics?${params}`),
          fetch(`${API}/api/analytics/pet-types?${params}`),
          fetch(`${API}/api/analytics/turnaround?${params}`),
          fetch(`${API}/api/analytics/geography?${params}`),
          fetch(`${API}/api/analytics/transportation?${params}`),
          fetch(`${API}/api/analytics/euthanasia`)
        ];
        const results = await Promise.all(endpoints);
        const [dashboard, referral, sellers, payment, vetClinics, petTypes, turnaround, geo, transport, euthanasia] = await Promise.all(results.map(r => r.ok ? r.json() : null));
        setAnalyticsData(dashboard);
        setReferralData(referral);
        setBestSellersData(sellers);
        setPaymentData(payment);
        setVetClinicData(vetClinics);
        setPetTypeData(petTypes);
        setTurnaroundData(turnaround);
        setGeoData(geo);
        setTransportData(transport);
        setEuthanasiaData(euthanasia);
      } catch (err) {
        setError(`Error: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [dateRange]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ filter_type: exportFilter });
      if (exportFilter !== 'all') {
        params.append('start_date', dateRange.start);
        params.append('end_date', dateRange.end);
      }
      const response = await fetch(`${API}/api/export/pets-csv?${params}`);
      if (!response.ok) throw new Error(`Export failed: ${response.status}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `pure_souls_pets_${exportFilter}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(blobUrl); }, 500);
      showToast('CSV exported successfully!', 'success');
      setShowExportOptions(false);
    } catch (error) {
      showToast(`Export failed: ${error.message}`, 'error');
    } finally {
      setExporting(false);
    }
  };

  const fmt$ = (amount) => new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', minimumFractionDigits: 0 }).format(amount);
  const fmtRaw = (amount) => (amount || 0).toFixed(2);
  const toggle = (key) => setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleReport = (key) => setSelectedReports(prev => ({ ...prev, [key]: !prev[key] }));

  const BUSINESS_REPORTS = [
    { key: 'vet_clinics', label: 'Vet Clinic Performance' },
    { key: 'referral_sources', label: 'Referral Sources' },
    { key: 'monthly_performance', label: 'Monthly Performance' },
    { key: 'payment_methods', label: 'Payment Methods' },
    { key: 'pet_types', label: 'Pet Types & Breeds' },
    { key: 'postcodes', label: 'Top Postcodes' },
    { key: 'turnaround', label: 'Turnaround Time' },
  ];

  const handleBusinessExport = () => {
    setBusinessExporting(true);
    try {
      let csv = `HOLONS Business Report\nDate Range: ${dateRange.start} to ${dateRange.end}\nGenerated: ${new Date().toLocaleDateString('en-AU')}\n\n`;

      if (selectedReports.vet_clinics && vetClinicData?.clinics) {
        csv += `=== VET CLINIC PERFORMANCE ===\n`;
        csv += `Clinic,Referrals,Revenue,Avg Spend,Share %,Growth %,Last Referral\n`;
        Object.entries(vetClinicData.clinics).forEach(([name, d]) => {
          csv += `"${name}",${d.referral_count},${fmtRaw(d.total_revenue)},${fmtRaw(d.avg_spend)},${(d.share_pct || 0).toFixed(1)},${d.growth_pct || 0},"${d.last_referral ? d.last_referral.substring(0, 10) : ''}"\n`;
        });
        csv += `\n`;
      }

      if (selectedReports.referral_sources && referralData?.referral_sources) {
        csv += `=== REFERRAL SOURCES ===\n`;
        csv += `Source,Count,Revenue,Avg Spend,%\n`;
        Object.entries(referralData.referral_sources).forEach(([source, d]) => {
          csv += `"${source}",${d.count},${fmtRaw(d.total_revenue)},${fmtRaw(d.average_spend)},${(d.percentage || 0).toFixed(1)}\n`;
        });
        csv += `\n`;
      }

      if (selectedReports.monthly_performance && analyticsData?.analytics?.monthly_breakdown) {
        csv += `=== MONTHLY PERFORMANCE ===\n`;
        csv += `Month,Customers,Revenue,Avg Spend\n`;
        Object.entries(analyticsData.analytics.monthly_breakdown)
          .sort(([a], [b]) => a.localeCompare(b))
          .forEach(([month, d]) => {
            csv += `${month},${d.count},${fmtRaw(d.revenue)},${d.count > 0 ? fmtRaw(d.revenue / d.count) : '0.00'}\n`;
          });
        csv += `\n`;
      }

      if (selectedReports.payment_methods && paymentData?.payment_methods) {
        csv += `=== PAYMENT METHODS ===\n`;
        csv += `Method,Count,%\n`;
        Object.entries(paymentData.payment_methods).forEach(([method, d]) => {
          csv += `"${method}",${d.count},${(d.percentage || 0).toFixed(1)}\n`;
        });
        csv += `\n`;
      }

      if (selectedReports.pet_types && petTypeData) {
        csv += `=== PET TYPE BREAKDOWN ===\n`;
        csv += `Type,Count,%,Avg Weight (kg)\n`;
        Object.entries(petTypeData.pet_types || {}).forEach(([type, d]) => {
          csv += `${type},${d.count},${d.percentage},${d.avg_weight}\n`;
        });
        csv += `\n`;
        if (petTypeData.top_breeds && Object.keys(petTypeData.top_breeds).length > 0) {
          csv += `=== TOP BREEDS ===\n`;
          csv += `Breed,Count\n`;
          Object.entries(petTypeData.top_breeds).forEach(([breed, count]) => {
            csv += `"${breed}",${count}\n`;
          });
          csv += `\n`;
        }
        if (petTypeData.weight_distribution) {
          csv += `=== WEIGHT DISTRIBUTION ===\n`;
          csv += `Range,Count\n`;
          Object.entries(petTypeData.weight_distribution).forEach(([range, count]) => {
            csv += `${range},${count}\n`;
          });
          csv += `\n`;
        }
      }

      if (selectedReports.postcodes && geoData?.top_areas) {
        csv += `=== TOP POSTCODES ===\n`;
        csv += `Postcode,Customers,Revenue,%\n`;
        Object.entries(geoData.postcodes || geoData.top_areas).forEach(([pc, d]) => {
          csv += `${pc},${d.count},${fmtRaw(d.revenue)},${d.percentage}\n`;
        });
        csv += `\n`;
      }

      if (selectedReports.turnaround && turnaroundData) {
        csv += `=== TURNAROUND TIME ===\n`;
        csv += `Metric,Value\n`;
        csv += `Completed Pets,${turnaroundData.completed_pets}\n`;
        csv += `Average Days,${turnaroundData.avg_turnaround_days}\n`;
        csv += `Fastest (days),${turnaroundData.min_days}\n`;
        csv += `Slowest (days),${turnaroundData.max_days}\n`;
        if (turnaroundData.by_weight_range) {
          Object.entries(turnaroundData.by_weight_range).forEach(([range, avg]) => {
            csv += `Avg Days (${range}),${avg}\n`;
          });
        }
        csv += `\n`;
      }

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pure_souls_business_report_${dateRange.start}_to_${dateRange.end}.csv`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 500);
      showToast('Business report exported!', 'success');
    } catch (err) {
      showToast(`Export failed: ${err.message}`, 'error');
    } finally {
      setBusinessExporting(false);
    }
  };

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'vet_clinics', label: 'Vet Clinics' },
    { key: 'referrals', label: 'Referrals' },
    { key: 'products', label: 'Products' },
    { key: 'insights', label: 'Insights' },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={onClose}>
      <div className="bg-white w-full h-full sm:h-auto sm:max-h-[95vh] sm:rounded-lg sm:max-w-5xl sm:mx-auto sm:mt-4 overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="analytics-modal">
        {/* Header - always visible */}
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between shrink-0">
          <h2 className="text-lg font-bold text-gray-900">Analytics Dashboard</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 text-lg" data-testid="analytics-close-btn">&times;</button>
        </div>

        {/* Filters - compact, scrollable if needed */}
        <div className="px-4 py-2 border-b border-gray-100 shrink-0 space-y-2">
          {/* Date Presets */}
          <div className="flex flex-wrap gap-1.5">
            {[
              { label: 'This Week', key: 'this_week' },
              { label: 'Last Week', key: 'last_week' },
              { label: 'This Month', key: 'this_month' },
              { label: 'Last Month', key: 'last_month' },
              { label: 'Last 30', key: 'last_30' },
              { label: 'Year', key: 'this_year' },
              { label: 'All', key: 'all_time' },
            ].map(({ label, key }) => {
              const preset = getPreset(key);
              const isActive = dateRange.start === preset.start && dateRange.end === preset.end;
              return (
                <button key={key} onClick={() => setDateRange(preset)}
                  className={`px-2 py-0.5 text-xs font-medium rounded-full transition ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-blue-50'}`}>
                  {label}
                </button>
              );
            })}
          </div>
          {/* Custom date inputs + Tabs on same row */}
          <div className="flex items-center gap-2 justify-between">
            <div className="flex items-center gap-1">
              <input type="date" value={dateRange.start} onChange={e => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="text-xs border border-gray-300 rounded px-1.5 py-0.5 w-28" />
              <span className="text-gray-400 text-xs">-</span>
              <input type="date" value={dateRange.end} onChange={e => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="text-xs border border-gray-300 rounded px-1.5 py-0.5 w-28" />
            </div>
          </div>
          {/* Tabs */}
          <div className="flex gap-1 overflow-x-auto pb-1 -mr-4 sm:-mr-6">
            {TABS.map(t => (
              <button key={t.key} onClick={() => setActiveTab(t.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg whitespace-nowrap transition ${activeTab === t.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {t.label}
              </button>
            ))}
            <div className="shrink-0 w-6">&nbsp;</div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 pb-20 space-y-4">
          {loading && (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-3"></div>
              <p className="text-gray-500 text-sm">Loading analytics...</p>
            </div>
          )}
          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600 text-sm">{error}</div>}

          {!loading && !error && (
            <>
              {/* ===== OVERVIEW TAB ===== */}
              {activeTab === 'overview' && (
                <div className="space-y-4">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <SummaryCard label="Customers" value={analyticsData?.analytics?.summary?.total_customers || 0} color="blue" />
                    <SummaryCard label="Revenue" value={fmt$(analyticsData?.analytics?.summary?.total_revenue || 0)} color="green" />
                    <SummaryCard label="Avg Spend" value={fmt$(analyticsData?.analytics?.summary?.average_spend || 0)} color="purple" />
                    <SummaryCard label="Avg Turnaround" value={`${turnaroundData?.avg_turnaround_days || 0} days`} color="orange" />
                  </div>

                  {/* Pet Type Breakdown */}
                  {petTypeData?.pet_types && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Pet Type Breakdown</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.entries(petTypeData.pet_types).map(([type, data]) => (
                          <div key={type} className="bg-gray-50 rounded-lg p-3">
                            <p className="text-lg font-bold text-gray-900">{data.count}</p>
                            <p className="text-xs text-gray-600">{type} ({data.percentage}%)</p>
                            <p className="text-xs text-gray-400">Avg {data.avg_weight}kg</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* 🚚 Transportation Revenue */}
                  {transportData?.success && (
                    <div className="bg-white rounded-lg border p-4" data-testid="transportation-card">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">🚚 Transportation Revenue</h3>
                        <div className="text-right">
                          <p className="text-lg font-bold text-blue-700" data-testid="transportation-total-revenue">
                            {fmt$(transportData.total_revenue || 0)}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            {transportData.total_movements || 0} movements
                            {transportData.home_collection_count > 0
                              ? ` · ${transportData.home_collection_count} home pick-ups`
                              : ''}
                          </p>
                        </div>
                      </div>
                      {transportData.breakdown && transportData.breakdown.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-500 border-b">
                                <th className="py-1.5 text-left font-medium">Type</th>
                                <th className="py-1.5 text-center font-medium">Qty</th>
                                <th className="py-1.5 text-right font-medium">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {transportData.breakdown.map((row) => (
                                <tr key={row.label} className="border-b last:border-0">
                                  <td className="py-1.5 pr-2">{row.label}</td>
                                  <td className="py-1.5 text-center">{row.count}</td>
                                  <td className="py-1.5 text-right font-semibold text-gray-800">{fmt$(row.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500 italic py-2">No transportation movements in this period.</p>
                      )}
                    </div>
                  )}

                  {/* 🕊️ Euthanasia (add-on) */}
                  {euthanasiaData?.success && (
                    <div className="bg-white rounded-lg border p-4" data-testid="euthanasia-card">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-900">🕊️ Euthanasia (add-on)</h3>
                        <p className="text-[11px] text-gray-400">Booked add-on service</p>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-rose-50 rounded-lg p-3" data-testid="euthanasia-this-week">
                          <p className="text-[11px] uppercase tracking-wide text-rose-400 font-medium">This Week</p>
                          <p className="text-lg font-bold text-rose-700">{fmt$(euthanasiaData.this_week?.revenue || 0)}</p>
                          <p className="text-xs text-gray-500">{euthanasiaData.this_week?.count || 0} euthanasia{(euthanasiaData.this_week?.count || 0) === 1 ? '' : 's'}</p>
                        </div>
                        <div className="bg-rose-50 rounded-lg p-3" data-testid="euthanasia-this-month">
                          <p className="text-[11px] uppercase tracking-wide text-rose-400 font-medium">This Month</p>
                          <p className="text-lg font-bold text-rose-700">{fmt$(euthanasiaData.this_month?.revenue || 0)}</p>
                          <p className="text-xs text-gray-500">{euthanasiaData.this_month?.count || 0} euthanasia{(euthanasiaData.this_month?.count || 0) === 1 ? '' : 's'}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Weight Distribution */}
                  {petTypeData?.weight_distribution && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Weight Distribution</h3>
                      <div className="space-y-2">
                        {Object.entries(petTypeData.weight_distribution).map(([range, count]) => {
                          const max = Math.max(...Object.values(petTypeData.weight_distribution));
                          const pct = max > 0 ? (count / max) * 100 : 0;
                          return (
                            <div key={range} className="flex items-center gap-2">
                              <span className="text-xs text-gray-600 w-12 shrink-0">{range}</span>
                              <div className="flex-1 bg-gray-100 rounded-full h-4 min-w-0">
                                <div className="bg-blue-500 h-4 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                              </div>
                              <span className="text-xs font-medium text-gray-700 w-8 text-right shrink-0">{count}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Top Breeds */}
                  {petTypeData?.top_breeds && Object.keys(petTypeData.top_breeds).length > 0 && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Breeds</h3>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(petTypeData.top_breeds).map(([breed, count]) => (
                          <span key={breed} className="text-xs bg-blue-50 text-blue-700 px-2.5 py-1 rounded-full border border-blue-200">
                            {breed} ({count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Monthly Performance */}
                  {analyticsData?.analytics?.monthly_breakdown && Object.keys(analyticsData.analytics.monthly_breakdown).length > 0 && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Monthly Performance</h3>
                      <div className="space-y-2">
                        {Object.entries(analyticsData.analytics.monthly_breakdown)
                          .sort(([a], [b]) => b.localeCompare(a))
                          .map(([month, data]) => {
                            const maxRev = Math.max(...Object.values(analyticsData.analytics.monthly_breakdown).map(d => d.revenue));
                            const pct = maxRev > 0 ? (data.revenue / maxRev) * 100 : 0;
                            return (
                              <div key={month} className="flex items-center gap-2">
                                <span className="text-xs text-gray-600 w-14 shrink-0">{month}</span>
                                <div className="flex-1 bg-gray-100 rounded-full h-4 min-w-0">
                                  <div className="bg-green-500 h-4 rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                                </div>
                                <span className="text-xs text-gray-500 w-6 text-right shrink-0">{data.count}</span>
                                <span className="text-xs font-medium text-green-700 w-14 text-right shrink-0">{fmt$(data.revenue)}</span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}

                  {/* Customer Export Data */}
                  <div className="bg-gray-50 rounded-lg border p-4">
                    <div className="flex items-center gap-3">
                      <button onClick={() => { setShowExportOptions(!showExportOptions); setShowBusinessExport(false); }}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium shrink-0">
                        Export
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Customer Export Data</p>
                        <p className="text-xs text-gray-500">Download pet/client records as CSV</p>
                      </div>
                    </div>
                    {showExportOptions && (
                      <div className="mt-3 pt-3 border-t border-gray-200 flex items-center gap-2 flex-wrap">
                        {['all', 'active', 'completed'].map(f => (
                          <button key={f} onClick={() => setExportFilter(f)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium ${exportFilter === f ? 'bg-green-600 text-white' : 'bg-white border text-gray-600'}`}>
                            {f.charAt(0).toUpperCase() + f.slice(1)}
                          </button>
                        ))}
                        <button onClick={handleExportCSV} disabled={exporting}
                          className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-1.5 rounded-lg text-xs font-medium">
                          {exporting ? 'Exporting...' : 'Download'}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Business Export Data */}
                  <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
                    <div className="flex items-center gap-3">
                      <button onClick={() => { setShowBusinessExport(!showBusinessExport); setShowExportOptions(false); }}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium shrink-0">
                        Export
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900">Business Export Data</p>
                        <p className="text-xs text-gray-500">Analytics reports for {dateRange.start} to {dateRange.end}</p>
                      </div>
                    </div>
                    {showBusinessExport && (
                      <div className="mt-3 pt-3 border-t border-blue-200">
                        <p className="text-xs font-medium text-gray-700 mb-2">Select reports to include:</p>
                        <div className="grid grid-cols-2 gap-1.5 mb-3">
                          {BUSINESS_REPORTS.map(({ key, label }) => (
                            <label key={key} className="flex items-center gap-2 cursor-pointer text-xs text-gray-700 hover:text-blue-700">
                              <input type="checkbox" checked={selectedReports[key]} onChange={() => toggleReport(key)}
                                className="w-3.5 h-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                              {label}
                            </label>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <button onClick={() => setSelectedReports(Object.fromEntries(BUSINESS_REPORTS.map(r => [r.key, true])))}
                            className="text-xs text-blue-600 hover:underline">Select All</button>
                          <span className="text-gray-300">|</span>
                          <button onClick={() => setSelectedReports(Object.fromEntries(BUSINESS_REPORTS.map(r => [r.key, false])))}
                            className="text-xs text-blue-600 hover:underline">Clear All</button>
                          <button onClick={handleBusinessExport} disabled={businessExporting || !Object.values(selectedReports).some(v => v)}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-1.5 rounded-lg text-xs font-medium">
                            {businessExporting ? 'Exporting...' : 'Download Selected'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ===== VET CLINICS TAB ===== */}
              {activeTab === 'vet_clinics' && vetClinicData && (
                <div className="space-y-4">
                  {/* Vet Summary Cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <SummaryCard label="Vet Referrals" value={vetClinicData.total_vet_referrals || 0} color="teal" />
                    <SummaryCard label="Active Clinics" value={vetClinicData.total_clinics_active || 0} color="indigo" />
                  </div>

                  {/* Clinic Leaderboard */}
                  <div className="bg-white rounded-lg border p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Clinic Leaderboard</h3>
                    <div className="space-y-2">
                      {Object.entries(vetClinicData.clinics || {}).map(([name, data], idx) => (
                        <div key={name} className="border rounded-lg p-3 hover:bg-gray-50 transition">
                          <div className="flex items-center justify-between mb-1">
                            <div className="flex items-center gap-2">
                              <span className={`text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center ${idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                                {idx + 1}
                              </span>
                              <span className="text-sm font-semibold text-gray-900">{name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {data.growth_pct !== undefined && data.prev_period_count > 0 && (
                                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${data.growth_pct > 0 ? 'bg-green-100 text-green-700' : data.growth_pct < 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`}>
                                  {data.growth_pct > 0 ? '+' : ''}{data.growth_pct}%
                                </span>
                              )}
                              <span className="text-sm font-bold text-blue-700">{data.referral_count}</span>
                            </div>
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500">
                            <span>Revenue: {fmt$(data.total_revenue)} · Avg: {fmt$(data.avg_spend)}</span>
                            <span>{data.share_pct?.toFixed(1)}% share</span>
                          </div>
                          {/* Mini bar */}
                          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
                            <div className="bg-teal-500 h-1.5 rounded-full" style={{ width: `${data.share_pct || 0}%` }}></div>
                          </div>

                          {/* Expandable detail */}
                          <button onClick={() => toggle(`clinic_${name}`)} className="text-xs text-blue-600 mt-2 hover:underline">
                            {expandedSections[`clinic_${name}`] ? 'Hide details' : 'Show details'}
                          </button>
                          {expandedSections[`clinic_${name}`] && (
                            <div className="mt-2 pt-2 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <p className="text-gray-500">Pet Types</p>
                                {Object.entries(data.pet_types || {}).map(([pt, count]) => (
                                  <span key={pt} className="inline-block bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded mr-1 mt-1">{pt}: {count}</span>
                                ))}
                              </div>
                              <div>
                                <p className="text-gray-500">Monthly Trend</p>
                                {Object.entries(data.monthly_trend || {}).slice(-4).map(([m, c]) => (
                                  <span key={m} className="inline-block bg-gray-50 text-gray-600 px-1.5 py-0.5 rounded mr-1 mt-1">{m}: {c}</span>
                                ))}
                              </div>
                              {data.last_referral && (
                                <div className="col-span-2">
                                  <p className="text-gray-500">Last Referral: <span className="text-gray-700">{data.last_referral.substring(0, 10)}</span></p>
                                </div>
                              )}
                              {data.nq_care_payments > 0 && (
                                <div className="col-span-2">
                                  <p className="text-amber-700">Clinic Account Payments: {data.nq_care_payments} ({fmt$(data.nq_care_revenue)})</p>
                                </div>
                              )}
                              {data.prev_period_count > 0 && (
                                <div className="col-span-2">
                                  <p className="text-gray-500">Previous period: {data.prev_period_count} referrals</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* ===== REFERRALS TAB ===== */}
              {activeTab === 'referrals' && (
                <div className="space-y-4">
                  {referralData?.referral_sources && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Referral Sources ({referralData.total_customers} customers)</h3>
                      <div className="space-y-2">
                        {Object.entries(referralData.referral_sources).map(([source, data]) => (
                          <div key={source} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900">{source}</p>
                              <p className="text-xs text-gray-500">{data.count} customers · {data.percentage?.toFixed(1)}%</p>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-sm font-semibold text-gray-900">{fmt$(data.total_revenue)}</p>
                              <p className="text-xs text-gray-500">Avg: {fmt$(data.average_spend)}</p>
                            </div>
                            <div className="w-16 shrink-0">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${Math.min(data.percentage || 0, 100)}%` }}></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment Methods */}
                  {paymentData?.payment_methods && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Payment Methods ({paymentData.total_transactions} transactions)</h3>
                      <div className="space-y-2">
                        {Object.entries(paymentData.payment_methods).map(([method, data]) => (
                          <div key={method} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-lg">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900">{method}</p>
                            </div>
                            <span className="text-sm font-semibold text-gray-700">{data.count}</span>
                            <span className="text-xs text-gray-500 w-12 text-right">{data.percentage?.toFixed(1)}%</span>
                            <div className="w-16">
                              <div className="w-full bg-gray-200 rounded-full h-2">
                                <div className="bg-orange-500 h-2 rounded-full" style={{ width: `${Math.min(data.percentage || 0, 100)}%` }}></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ===== PRODUCTS TAB ===== */}
              {activeTab === 'products' && bestSellersData?.best_sellers && (
                <div className="space-y-4">
                  {['urns', 'keepsakes', 'jewelry'].map(cat => {
                    const items = bestSellersData.best_sellers[cat] || {};
                    if (Object.keys(items).length === 0) return null;
                    const colors = { urns: 'purple', keepsakes: 'blue', jewelry: 'amber' };
                    const icons = { urns: 'Urns', keepsakes: 'Keepsakes', jewelry: 'Jewelry' };
                    const c = colors[cat];
                    return (
                      <div key={cat} className={`bg-${c}-50 rounded-lg border border-${c}-200 p-4`}>
                        <h3 className={`text-sm font-semibold text-${c}-900 mb-3`}>Top {icons[cat]}</h3>
                        <div className="space-y-2">
                          {Object.entries(items).slice(0, 8).map(([name, data]) => (
                            <div key={name} className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                                <p className="text-xs text-gray-500">{data.count} sold</p>
                              </div>
                              <p className={`text-sm font-semibold text-${c}-700`}>{fmt$(data.total_revenue || data.unit_price * data.count)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ===== INSIGHTS TAB ===== */}
              {activeTab === 'insights' && (
                <div className="space-y-4">
                  {/* Turnaround */}
                  {turnaroundData && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Turnaround Time (Completed Pets)</h3>
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-blue-700">{turnaroundData.avg_turnaround_days}</p>
                          <p className="text-xs text-blue-600">Avg Days</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-green-700">{turnaroundData.min_days}</p>
                          <p className="text-xs text-green-600">Fastest</p>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-3 text-center">
                          <p className="text-lg font-bold text-orange-700">{turnaroundData.max_days}</p>
                          <p className="text-xs text-orange-600">Slowest</p>
                        </div>
                      </div>
                      {turnaroundData.by_weight_range && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-2">By Weight Range</p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                            {Object.entries(turnaroundData.by_weight_range).map(([range, avg]) => (
                              <div key={range} className="bg-gray-50 rounded p-2 text-center">
                                <p className="text-sm font-semibold text-gray-700">{avg} days</p>
                                <p className="text-xs text-gray-500">{range}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Geography */}
                  {geoData?.top_areas && Object.keys(geoData.top_areas).length > 0 && (
                    <div className="bg-white rounded-lg border p-4">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Top Postcodes ({geoData.total_customers} customers)</h3>
                      <div className="space-y-2">
                        {Object.entries(geoData.top_areas).map(([pc, data]) => {
                          const maxCount = Math.max(...Object.values(geoData.top_areas).map(d => d.count));
                          return (
                            <div key={pc} className="flex items-center gap-3">
                              <span className="text-xs font-mono text-gray-700 w-10">{pc}</span>
                              <div className="flex-1 bg-gray-100 rounded-full h-3">
                                <div className="bg-indigo-500 h-3 rounded-full" style={{ width: `${(data.count / maxCount) * 100}%` }}></div>
                              </div>
                              <span className="text-xs text-gray-600 w-20 text-right">{data.count} ({data.percentage}%)</span>
                              <span className="text-xs font-medium text-gray-700 w-14 text-right">{fmt$(data.revenue)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, color }) => {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-900',
    green: 'bg-green-50 border-green-200 text-green-900',
    purple: 'bg-purple-50 border-purple-200 text-purple-900',
    orange: 'bg-orange-50 border-orange-200 text-orange-900',
    teal: 'bg-teal-50 border-teal-200 text-teal-900',
    indigo: 'bg-indigo-50 border-indigo-200 text-indigo-900',
  };
  return (
    <div className={`rounded-lg border p-3 ${colors[color] || colors.blue}`} data-testid={`summary-card-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <p className="text-xs font-medium opacity-70">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </div>
  );
};

export default SimpleAnalyticsView;
